'use client';

import { useEffect, useRef } from 'react';

import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';

import useTimelineStore from '../../lib/store/timeline';

export const defaultElement: React.ElementType = 'section' as const;
export type AllowedElements = typeof defaultElement | keyof React.ElementType;

export type DefaultAsType = typeof defaultElement;

export type Props<E extends AllowedElements> = {
  as?: E;

  id: string;
  title: string;
  skipStore?: boolean;
} & React.PropsWithChildren &
  React.ComponentPropsWithRef<E>;

const TimelineItem = <E extends AllowedElements = typeof defaultElement>({
  as: asComponent,
  id,
  title,
  skipStore = false,
  children,
  ...rest
}: Props<E>) => {
  const Component = asComponent || defaultElement;
  const ref = useRef<HTMLElement>(null);

  const addSection = useTimelineStore((state) => state.addSection);
  const removeSection = useTimelineStore((state) => state.removeSection);

  const isDesktop = useHydrationSafeResponsive('desktop');

  useEffect(() => {
    if (!skipStore) {
      addSection({ id, title, ref });

      return () => removeSection(id);
    }
  }, [addSection, id, removeSection, skipStore, title]);

  return isDesktop ? (
    <Component ref={ref} {...rest}>
      {children}
    </Component>
  ) : (
    children
  );
};

export default TimelineItem;
